import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * One-tap PWA install:
 * - Registers /sw.js
 * - Captures the `beforeinstallprompt` event on Chrome/Android/Edge
 * - Shows a small floating CTA "Install ScottsClub" until accepted or dismissed
 * - On iOS Safari (no install event), shows a one-line hint with the Add-to-Home-Screen instructions
 */
export default function PwaInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [show, setShow] = useState(false);
    const [iosHint, setIosHint] = useState(false);

    useEffect(() => {
        if ("serviceWorker" in navigator) {
            window.addEventListener("load", () => {
                navigator.serviceWorker.register("/sw.js").catch(() => { /* ignore */ });
            });
        }

        // Never surface the public install prompt on admin / staff routes —
        // those flows have their own admin-app install banner.
        const path = window.location.pathname || "";
        if (path.startsWith("/admin") || path.startsWith("/staff") || path.startsWith("/scan")) {
            return undefined;
        }

        const dismissed = localStorage.getItem("sc_pwa_dismissed") === "1";
        const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
        if (standalone || dismissed) return undefined;

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
            setShow(true);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);

        // iOS detection — Safari doesn't fire beforeinstallprompt
        const ua = window.navigator.userAgent || "";
        const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
        if (isIos && isSafari) {
            const lastSeen = Number(localStorage.getItem("sc_pwa_ios_seen") || 0);
            if (Date.now() - lastSeen > 7 * 24 * 3600 * 1000) {
                setIosHint(true);
            }
        }

        return () => window.removeEventListener("beforeinstallprompt", onPrompt);
    }, []);

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice?.outcome === "accepted") {
            setShow(false);
        }
        setDeferred(null);
    };

    const dismiss = () => {
        setShow(false);
        setIosHint(false);
        localStorage.setItem("sc_pwa_dismissed", "1");
        localStorage.setItem("sc_pwa_ios_seen", String(Date.now()));
    };

    if (!show && !iosHint) return null;

    return (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,420px)]" data-testid="pwa-install">
            <div className="glass-card rounded-sm p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center text-gold flex-shrink-0">
                    <Download size={16} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-serif text-base text-ink-primary leading-tight">Install ScottsClub</div>
                    <div className="text-[11px] text-ink-secondary mt-0.5 leading-tight">
                        {iosHint
                            ? "Tap the Share icon, then \u201CAdd to Home Screen\u201D."
                            : "Open the club app from your home screen."}
                    </div>
                </div>
                {!iosHint && (
                    <button
                        onClick={install}
                        data-testid="pwa-install-btn"
                        className="btn-gold inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-[11px] tracking-[0.2em] uppercase font-medium"
                    >
                        Install
                    </button>
                )}
                <button
                    onClick={dismiss}
                    data-testid="pwa-dismiss"
                    aria-label="Dismiss"
                    className="text-ink-secondary hover:text-gold"
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    );
}
